import { useMemo } from 'react'
import ActiveJobsCard from '../../personal/components/ActiveJobsCard'
import ActivityTimelineCard from '../../personal/components/ActivityTimelineCard'
import CommunicationsCard from '../../personal/components/CommunicationsCard'
import ContributionPointsCard from '../../personal/components/ContributionPointsCard'
import CriticalWorkstreamCard from '../../personal/components/CriticalWorkstreamCard'
import ProductivityCard from '../../personal/components/ProductivityCard'
import ScheduleCard from '../../personal/components/ScheduleCard'
import TimesheetProgressCard from '../../personal/components/TimesheetProgressCard'
import UpcomingPaymentsCard from '../../personal/components/UpcomingPaymentsCard'
import AppDateInput from '../../../../components/AppDateInput'
import AppDropdown from '../../../../components/AppDropdown'
import { parseStoredUser } from '../../../../utils/authSession'
import { WidgetSkeleton } from '../../../../components/skeleton'

function DashboardHeader({
  heading,
  dateLabel,
  priorityLabel,
  filters,
  options,
  onDateChange,
  onCompanyChange,
  onProjectChange,
}) {
  return (
    <section className="mb-10 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
      <div>
        <h1 className="mb-2 text-6xl font-extrabold tracking-tight text-primary">{heading}</h1>
        <p className="font-medium text-on-surface-variant">
          {dateLabel} <span className="px-2 text-on-surface">-</span>
          <span className="font-bold text-primary">{priorityLabel}</span>
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg bg-surface-container-low p-3">
        <label className="grid min-w-[170px] gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Today</span>
          <AppDateInput
            value={filters.selectedDate}
            onChange={onDateChange}
            inputClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface shadow-none"
          />
        </label>

        <label className="grid min-w-[190px] gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Company</span>
          <AppDropdown
            value={filters.companyId}
            onChange={onCompanyChange}
            placeholder="All Companies"
            triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
            options={[
              { value: '', label: 'All Companies' },
              ...options.companies.map((company) => ({
                value: String(company.id),
                label: company.name,
              })),
            ]}
          />
        </label>

        <label className="grid min-w-[190px] gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-on-surface-variant">Project</span>
          <AppDropdown
            value={filters.projectId}
            onChange={onProjectChange}
            placeholder="All Projects"
            triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
            options={[
              { value: '', label: 'All Projects' },
              ...options.projects.map((project) => ({
                value: String(project.id),
                label: project.name,
              })),
            ]}
          />
        </label>

        <button
          type="button"
          onClick={() => {
            onDateChange('')
            onCompanyChange('')
            onProjectChange('')
          }}
          className="rounded-md px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-primary transition-colors hover:bg-primary/10"
        >
          Reset
        </button>
      </div>
    </section>
  )
}

function DashboardFooter() {
  return (
    <footer className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-surface-variant/30 py-6 lg:flex-row lg:items-center">
      <div className="flex items-center gap-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
          Bridge Enterprise OS v4.2.0
        </p>
        <div className="h-1 w-1 rounded-full bg-outline-variant" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Privacy Policy</p>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase text-on-surface-variant">System Status:</span>
        <div className="flex items-center gap-1.5 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-800">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-600" />
          Operational
        </div>
      </div>
    </footer>
  )
}

function FloatingActionButton() {
  return (
    <button
      type="button"
      className="group fixed bottom-8 right-8 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-container text-on-primary shadow-2xl transition-transform hover:scale-105 active:scale-95"
    >
      <span className="material-symbols-outlined text-4xl transition-transform duration-300 group-hover:rotate-90">
        add
      </span>
    </button>
  )
}

export default function DashboardCanvas({
  view,
  filters,
  options,
  onDateChange,
  onCompanyChange,
  onProjectChange,
  onOpenActionCenter,
}) {
  // ── Derive active modules once (zero network calls — reads JWT / localStorage) ──
  const activeModules = useMemo(() => parseStoredUser().modules || [], [])
  const hasBruno  = activeModules.includes('bruno')
  const hasNerve  = activeModules.includes('nerve')
  const hasBridge = activeModules.includes('bridge')

  // When Nerve is inactive the middle column (workstream + jobs) is entirely empty.
  // Shift to a 2-column grid so the remaining cards fill the space elegantly.
  const hasMiddleCol = hasNerve
  const col1Span = hasMiddleCol ? 'lg:col-span-4' : 'lg:col-span-5'
  const col2Span = 'lg:col-span-5'
  const col3Span = hasMiddleCol ? 'lg:col-span-3' : 'lg:col-span-7'

  return (
    <div className="mx-auto max-w-[1600px] pb-14">
      <DashboardHeader
        heading={view?.heading || 'Personal Dashboard'}
        dateLabel={view?.dateLabel || 'Today'}
        priorityLabel={view?.priorityLabel || 'Active Overview'}
        filters={filters}
        options={options}
        onDateChange={onDateChange}
        onCompanyChange={onCompanyChange}
        onProjectChange={onProjectChange}
      />

      <section className="grid grid-cols-12 gap-6">
        {/* ── Column 1: Bruno + Nerve widgets ─────────────────────────── */}
        {(hasBruno || hasNerve) && (
          <div className={`col-span-12 flex flex-col gap-6 ${col1Span}`}>
            {hasBruno && (view?.productivity ? <ProductivityCard {...view.productivity} /> : <WidgetSkeleton titleWidth="140px" height="180px" />)}
            {hasNerve && (view?.contribution ? <ContributionPointsCard {...view.contribution} /> : <WidgetSkeleton titleWidth="160px" height="150px" />)}
            {hasNerve && (view?.timesheetProgress ? <TimesheetProgressCard {...view.timesheetProgress} /> : <WidgetSkeleton titleWidth="170px" height="170px" />)}
            {hasNerve && (view?.payments ? <UpcomingPaymentsCard payments={view.payments} /> : <WidgetSkeleton titleWidth="150px" height="150px" />)}
          </div>
        )}

        {/* ── Column 2: Nerve-only widgets (Workstream + Jobs) ─────────── */}
        {hasNerve && (
          <div className={`col-span-12 flex flex-col gap-6 ${col2Span}`}>
            {view ? (
              <CriticalWorkstreamCard
                items={view.workstream}
                queueItems={view.queueWorkstream}
                criticalTotal={view.workstreamTotal || 0}
                queueTotal={view.queueWorkstreamTotal || 0}
                filters={filters}
              />
            ) : (
              <WidgetSkeleton titleWidth="220px" height="320px" />
            )}
            {view?.jobs ? <ActiveJobsCard jobs={view.jobs} /> : <WidgetSkeleton titleWidth="150px" height="220px" />}
          </div>
        )}

        {/* ── Column 3: Bruno (Schedule) + Bridge (Communications) ─────── */}
        {(hasBruno || hasBridge) && (
          <div className={`col-span-12 flex flex-col gap-6 ${col3Span}`}>
            {hasBruno && (view?.schedule ? <ScheduleCard items={view.schedule} onOpenActionCenter={onOpenActionCenter} /> : <WidgetSkeleton titleWidth="130px" height="240px" />)}
            {hasBridge && (view?.communications ? <CommunicationsCard items={view.communications} /> : <WidgetSkeleton titleWidth="160px" height="240px" />)}
          </div>
        )}
      </section>

      {/* ── Activity Timeline (Bruno) ────────────────────────────────── */}
      {hasBruno && (
        view?.activityTimeline ? (
          <section className="mt-6">
            <ActivityTimelineCard {...view.activityTimeline} />
          </section>
        ) : (
          <section className="mt-6">
            <WidgetSkeleton titleWidth="180px" height="180px" />
          </section>
        )
      )}

      <DashboardFooter />
      <FloatingActionButton />
    </div>
  )
}

